import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fade,
  Grid,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../hooks';
import {
  getSubmissionsGroupedBySubTopic,
  getSubmissionsV2State,
} from '../../../store/V2/SubmissionsSlice';
import { TP_COLORS } from '../../../constants';
import { msToMinutesSeconds } from '../../../helper';
import {
  getScoreColor,
  getScoreStatus,
  getTimeIcon,
} from '../QuizPerformance/DetailedOverview/components/StudentPerformanceRow';
import SubTopicPerformanceTable from './SubTopicPerformanceTable';
import BasicLoader from '../../../components/Loaders/BasicLoader';

const typoProps: TypographyProps = {
  fontSize: '14px',
  color: TP_COLORS.gray.darken,
};

interface ITopicsAccordionProps {
  studentId?: string;
}

const TopicsAccordion: React.FC<ITopicsAccordionProps> = ({ studentId }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const urlSearchParams = new URLSearchParams(location.search);
  const urlTopic = urlSearchParams.get('topic');

  // State
  const [openedTopics, setOpenedTopics] = useState<string[]>([]);

  // Store
  const {
    studentSubmissionsData,
    courseSubmissionsData,
    groupedSubTopicsSubmissions,
    status,
  } = useAppSelector(getSubmissionsV2State);

  useEffect(() => {
    if (urlTopic) {
      setOpenedTopics([urlTopic]);
      if (studentId) {
        dispatch(
          getSubmissionsGroupedBySubTopic({
            topicId: urlTopic,
            studentId,
          })
        );
      }
    }
  }, []);

  const handleTopicExpand = (topicId: string) => {
    let newOpenedSections = [...openedTopics];

    const match = newOpenedSections.some((item) => item === topicId);

    if (match) {
      newOpenedSections = newOpenedSections.filter((item) => item !== topicId);
    } else {
      newOpenedSections.push(topicId);
      if (studentId) {
        dispatch(
          getSubmissionsGroupedBySubTopic({
            topicId,
            studentId,
          })
        );
      }
    }

    setOpenedTopics(newOpenedSections);
  };

  return (
    <Stack
      boxShadow="0px 14px 40px 0px #6E9DB22E"
      borderRadius="20px"
      p="0 0 16px 0"
    >
      {studentSubmissionsData?.groupedSubmissions.map((topicItem, index) => {
        const isExpanded = openedTopics.some(
          (itemId) => itemId === topicItem.topicId
        );

        const selectedTopicSubmission = groupedSubTopicsSubmissions.find(
          (item) => item.topicId === topicItem.topicId
        );
        const topicStats = courseSubmissionsData?.groupedSubmissions.find(
          (item) => item._id === topicItem.topicId
        );

        const courseAverageScore = topicStats?.averageScore || 0;
        const courseAverageTime = topicStats?.averageTime || 0;

        const timeDif = courseAverageTime - topicItem.averageTime;

        return (
          <Accordion
            key={topicItem.topicId}
            disableGutters
            elevation={0}
            expanded={isExpanded}
            onChange={() => handleTopicExpand(topicItem.topicId)}
            sx={{
              '&:first-of-type': {
                borderTopLeftRadius: '20px',
                borderTopRightRadius: '20px',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowDownIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{
                flexDirection: 'row-reverse',
                height: '80px',
                paddingLeft: '28px',
              }}
            >
              <Grid container>
                <Grid item xs={5} md={6}>
                  <Stack height="100%" justifyContent="center">
                    <Typography
                      fontWeight="600"
                      fontSize="18px"
                      marginLeft="10px"
                    >
                      {topicItem.topicName}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4} md={4}>
                  <Fade in={!isExpanded}>
                    <Stack>
                      <Typography {...typoProps}>Time Taken</Typography>
                      <Box display="flex">
                        <Typography fontSize="12px" color="#94A3B8">
                          {msToMinutesSeconds(topicItem.averageTime)}
                        </Typography>
                        {timeDif !== 0 && (
                          <Box
                            display="flex"
                            alignItems="center"
                            color={getScoreColor(
                              courseAverageTime,
                              topicItem.averageTime
                            )}
                          >
                            {getTimeIcon(timeDif)}
                            <Typography fontSize="12px" marginLeft="2px">
                              {msToMinutesSeconds(Math.abs(timeDif))}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Fade>
                </Grid>
                <Grid item xs={3} md={2}>
                  <Stack>
                    <Typography {...typoProps}>
                      {topicItem.averageScore}/20
                    </Typography>
                    <Typography
                      fontSize="12px"
                      color={getScoreColor(
                        topicItem.averageScore,
                        courseAverageScore
                      )}
                    >
                      {getScoreStatus(
                        topicItem.averageScore,
                        courseAverageScore
                      )}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px' }}>
              {!selectedTopicSubmission && status === 'loading' ? (
                <BasicLoader
                  containerProps={{
                    minHeight: '100px',
                    alignItems: 'center',
                  }}
                />
              ) : null}

              {selectedTopicSubmission?.subTopics.length ? (
                <SubTopicPerformanceTable topicId={topicItem.topicId} />
              ) : null}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default TopicsAccordion;
